import React, { Component } from 'react';

import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import styled from 'styled-components';

import { tableHelper } from '@builttocreate/engine-utils';

import FieldModes from '../../../../constants/FieldModes';
import FieldTableColumnTypes from '../../../../constants/FieldTableColumnTypes';
import FieldTypes from '../../../../constants/FieldTypes';
import computeStyleChanges from '../../../../lib/computeStyleChanges';
import { filterHiddenTableColumnsSelector } from '../../../../lib/tableHelper';
import BarcodeGenericField from '../BarcodeGenericField';
import DropdownGenericField from '../DropdownGenericField';
import FieldTitle from '../FieldTitle';
import FieldDecorators from '../FieldDecorators';
import ImageField from '../ImageField';
import MultiselectGenericField from '../MultiselectGenericField';
import SignatureField from '../SignatureField';
import TextField from '../TextField';
import DateField from '../DateField';
import BlockGenericField from '../BlockGenericField';
import InputGroupRowDecorators from './InputGroupRowDecorators';

import Icon from '../../../misc/Icon';
import ThemeIcons from '../../../../theme/Icons';
import ThemeContext from '../../../../theme/ThemeContext';


const { getCellOperand } = tableHelper;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRowActions = styled.div`
  flex-basis: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  padding: 2px;
`;


const StyledSelect = styled.select`
  width: 18px;
  height: 18px;
  margin-right: 6px;
  background-color: inherit;
  color: ${(props) => props.fontColor};
  border: 1px solid ${(props) => props.borderColor};
  border-radius: ${(props) => props.borderRadius}px;
  transition: all 0.15s ease-in-out;

  &:hover {
    cursor: pointer;
    outline: unset;
    border: 1px solid ${(props) => props.primaryColor};
  }

  &:focus, &:focus-visible {
    outline: unset;
    border: 1px solid ${(props) => props.borderColor};
  }

`;

const StyledTitleWrapper = styled.div`
  padding-top: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  svg{
    margin-right:15px;
  }
`;

const StyledTitle = styled.div`
  flex: 1;
  margin-bottom:4px;
`;

const StyledRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: ${(props) => (props.textOverflow !== 'ellipsis' ? 'stretch' : 'flex-end')};
  flex-wrap: wrap;
  background-color: ${(props) => props.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => props.borderRadius}px;
  padding: ${(props) => props.padding}px;
  ${(props) => typeof props.paddingRight === 'number' ? `padding-right: ${props.paddingRight}px;` : ''}
`;

const StyledFieldWrapper = styled.div`
  ${(props) => {
    if (!props.width) return 'flex: 1 1 0; min-width: 100px;';

    return `
      max-width: ${props.width}px;
      min-width: ${props.width}px;
    `;

  }}
  display: flex;
  align-items: flex-end;
  padding: ${(props) => (props.padding !== undefined ? `${props.padding}px` : '3px')};
  height: ${(props) => (props.height !== undefined ? `${props.height}px` : 'auto')};
  min-height: 25px;

  /* Calculate cell height: 25px content + 2*borderWidth for border-box */
  --cell-height: ${(props) => `calc(25px + ${(props.borderWidth || 1) * 2}px)`};

  /* ========== DROPDOWN CELL ========== */
  &[data-field-type="dropdown"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  &[data-field-type="dropdown"][data-text-overflow="ellipsis"] {
    & select,
    & > div > span[data-field-generic-select-readonly] {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
  &[data-field-type="dropdown"]:not([data-text-overflow="ellipsis"]) {
    & select,
    & [data-ig-wrap-display] {
      min-height: var(--cell-height);
      box-sizing: border-box;
    }
  }

  /* ========== TEXT CELL ========== */
  &[data-field-type="text"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  &[data-field-type="text"][data-text-overflow="ellipsis"] {
    & input {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
  &[data-field-type="text"]:not([data-text-overflow="ellipsis"]) {
    & input,
    & textarea {
      min-height: var(--cell-height);
      box-sizing: border-box;
      width: 100%;
    }
    & [data-ig-wrap-display] {
      min-height: var(--cell-height);
      box-sizing: border-box;
      overflow-wrap: break-word;
    }
  }

  /* ========== DATE CELL ========== */
  &[data-field-type="date"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  &[data-field-type="date"][data-text-overflow="ellipsis"] {
    & input,
    & > div > div > span:last-child {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: flex;
      align-items: center;
    }
  }
  &[data-field-type="date"]:not([data-text-overflow="ellipsis"]) {
    & input,
    & > div > div > span:last-child {
      min-height: var(--cell-height);
      box-sizing: border-box;
      display: flex;
      align-items: center;
    }
  }

  /* ========== BLOCK CELL ========== */
  &[data-field-type="block"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  &[data-field-type="block"][data-text-overflow="ellipsis"] {
    & input,
    & > div > div > div {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
      display: flex;
      align-items: center;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  &[data-field-type="block"]:not([data-text-overflow="ellipsis"]) {
    & input,
    & textarea {
      min-height: var(--cell-height);
      box-sizing: border-box;
      width: 100%;
    }
    & > div > div > div,
    & [data-ig-wrap-display] {
      min-height: var(--cell-height);
      box-sizing: border-box;
      overflow-wrap: break-word;
    }
  }

  /* ========== BARCODE CELL ========== */
  &[data-field-type="barcode"] {
    & > div {
      height: auto;
      width: 100%;
    }
    & svg {
      height: 16px;
      width: 16px;
    }
  }
  &[data-field-type="barcode"][data-text-overflow="ellipsis"] {
    & > div > div > div {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
      padding: 2px;
    }
    & input, & textarea {
      height: 100%;
      min-height: unset;
      box-sizing: border-box;
    }
  }
  &[data-field-type="barcode"]:not([data-text-overflow="ellipsis"]) {
    & > div > div > div {
      min-height: var(--cell-height);
      box-sizing: border-box;
      padding: 2px;
    }
    & input, & textarea {
      height: 100%;
      min-height: unset;
      box-sizing: border-box;
    }
  }

  /* ========== MULTISELECT CELL ========== */
  &[data-field-type="multiselect"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  &[data-field-type="multiselect"][data-text-overflow="ellipsis"] {
    & > div > div {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
  &[data-field-type="multiselect"]:not([data-text-overflow="ellipsis"]) {
    & > div > div {
      min-height: var(--cell-height);
      box-sizing: border-box;
    }
  }

  /* ========== IMAGE CELL (Can grow when has value) ========== */
  &[data-field-type="image"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  /* Fixed height when empty */
  &[data-field-type="image"][data-empty="true"] {
    & > div > div > div {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
  /* Can grow when has value */
  &[data-field-type="image"]:not([data-empty="true"]) {
    & > div > div > div {
      min-height: var(--cell-height);
      box-sizing: border-box;
    }
  }

  /* ========== SIGNATURE CELL (Can grow when has value) ========== */
  &[data-field-type="signature"] {
    & > div {
      height: auto;
      width: 100%;
    }
  }
  /* Fixed height when empty */
  &[data-field-type="signature"][data-empty="true"] {
    & > div > div,
    & > div > div > div {
      height: var(--cell-height);
      min-height: var(--cell-height);
      max-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
  /* Can grow when has value */
  &[data-field-type="signature"]:not([data-empty="true"]) {
    & > div > div,
    & > div > div > div {
      min-height: var(--cell-height);
      box-sizing: border-box;
    }
  }
`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

class InputGroupFieldRow extends Component {

  static propTypes = {

    view: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    row: PropTypes.object,
    rowIndex: PropTypes.number,
    rowPath: PropTypes.string,
    columns: PropTypes.array,
    focused: PropTypes.string,
    disabled: PropTypes.bool,
    rowActions: PropTypes.array,
    enableTitle: PropTypes.bool,
    enableMenu: PropTypes.bool,

    primaryColor: PropTypes.string,
    dangerColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Column title styles
    columnTitleFontSize: PropTypes.number,
    columnTitleFontColor: PropTypes.string,
    columnTitleTextAlign: PropTypes.string,
    columnTitleFontStyle: PropTypes.string,
    columnTitleFontWeight: PropTypes.string,
    columnTitleTextTransform: PropTypes.string,
    columnTitleTextDecoration: PropTypes.string,
    columnTitleBackgroundColor: PropTypes.string,
    columnTitlePadding: PropTypes.number,

    //Styles
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderRadius: PropTypes.number,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,

    fieldMode: PropTypes.string,
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    decorators: PropTypes.array,
    onDecoratorClick: PropTypes.func,

    enableRowDecorators: PropTypes.bool,
    rowDecorators: PropTypes.array,
    onRowDecoratorClick: PropTypes.func,

    //Action handlers
    onCellChange: PropTypes.func,
    onCellBlur: PropTypes.func,
    onCellFocus: PropTypes.func,
    onRowDelete: PropTypes.func,
    onRowDuplicate: PropTypes.func,
    onRowMoveUp: PropTypes.func,
    onRowMoveDown: PropTypes.func,
    onRowAddBelow: PropTypes.func,
    onLoadCompleted: PropTypes.func,

    displayRequiredIndicators: PropTypes.bool,
  }

  static contextType = ThemeContext;

  constructor(props) {
    super(props);

    this.state = {
      rowRef: null,
      rowLoaded: false,
      imageColumnsLoaded: {}
    };

  }

  shouldComponentUpdate(nextProps, nextState) {
    return shouldComponentUpdate(nextProps, this.props, nextState, this.state);
  }

  componentDidUpdate(prevProps) {

    const { view, row, columns, onLoadCompleted, fieldPositionColumns, fieldMode, docFields } = this.props;
    const { rowRef, imageColumnsLoaded, rowLoaded } = this.state;

    const filteredColumns = filterHiddenTableColumnsSelector({ columns, fieldPositionColumns, view, mode: fieldMode, docFields });
    const imageColumnsTotal = getTableImageColumns({ columns: filteredColumns }).length;

    if (onLoadCompleted && rowRef && !rowLoaded && ((Object.keys(imageColumnsLoaded).length === imageColumnsTotal) || imageColumnsTotal === 0)) {
      this.setState({ rowLoaded: true });
      onLoadCompleted(row._id, { height: rowRef.offsetHeight });
    }

    if (rowLoaded && !isEqual(row, prevProps.row)) {
      this.setState({ rowLoaded: false });
    }

  }

  render() {

    const { rowRef } = this.state;

    const {
      view,
      id,
      fieldType,
      fieldId,
      fieldIdentifier,
      fieldPositionId,
      title,

      row,
      rowIndex,
      rowPath,
      rowActions,
      columns,
      fieldPositionColumns,
      focused,
      disabled,
      enableTitle,
      enableMenu,
      enableSystemFilePicker,

      primaryColor,
      dangerColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Column title styles
      columnTitleFontSize,
      columnTitleFontColor,
      columnTitleTextAlign,
      columnTitleFontStyle,
      columnTitleFontWeight,
      columnTitleTextTransform,
      columnTitleTextDecoration,
      columnTitleBackgroundColor,
      columnTitlePadding,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderRadius,
      borderColor,
      borderWidth,
      backgroundColor,
      padding,

      //Action handlers
      onCellChange,
      onCellFocus,
      onCellBlur,
      onLoadCompleted,
      onCellFileUpload,
      onCellFileClick,
      onCellFileDelete,
      onCapture,
      fieldMode,
      decorators,
      onDecoratorClick,
      enableRowDecorators,
      rowDecorators,
      onRowDecoratorClick,
      displayRequiredIndicators
    } = this.props;

    const styles = {
      primaryColor,
      dangerColor,

      //Title styles
      titleFontSize: columnTitleFontSize,
      titleFontColor: columnTitleFontColor,
      titleTextAlign: columnTitleTextAlign,
      titleFontStyle: columnTitleFontStyle,
      titleFontWeight: columnTitleFontWeight,
      titleTextTransform: columnTitleTextTransform,
      titleTextDecoration: columnTitleTextDecoration,
      //Field Styles
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderWidth,
      borderRadius,
      backgroundColor,
      padding,
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const filteredColumns = filterHiddenTableColumnsSelector({ columns, fieldPositionColumns, view, mode: fieldMode, docFields: this.props.docFields });
    const cells = filteredColumns.map((column, columnIndex) => {

      const rowId = row._id;
      const fieldPositionColumn = fieldPositionColumns && fieldPositionColumns[column._id] ? fieldPositionColumns[column._id] : {};

      const cell = row.cells && row.cells[column._id] ? row.cells[column._id] : null;
      const calculatedCell = row.calculatedCells && row.calculatedCells[column._id] ? row.calculatedCells[column._id] : cell;

      const cellKey = `${rowPath}_${column._id}`;
      const cellPath = `${rowPath}_${column._id}`;
      const isFocused = focused === cellPath;

      const columnWidth = column.width;

      const inputGroupWrapMode = textOverflow !== 'ellipsis';
      const fieldWrapperStyles = { width: column.width, padding: columnTitlePadding, borderWidth };

      const testId = `${id}-${rowId}-${column._id}-${columnIndex}`;

      if (column.type === FieldTableColumnTypes.dropdown) {
        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="dropdown"
            data-text-overflow={textOverflow || ''}
            {...fieldWrapperStyles}
          >
            <DropdownGenericField
              id={testId}
              fieldId={column._id}
              type="text"
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={cell || ''}
              options={column.options}
              inputGroupWrapMode={inputGroupWrapMode}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {

                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);

              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      } else if (column.type === FieldTableColumnTypes.image) {

        if (!cell) styles.minHeight = 25;
        const isImageEmpty = !cell || (Array.isArray(cell) && cell.length === 0);

        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="image"
            data-text-overflow={textOverflow || ''}
            data-empty={isImageEmpty ? 'true' : undefined}
            {...fieldWrapperStyles}
          >

            <ImageField
              id={testId}
              type={FieldTypes.image} // Type is needed to only allow images for image field
              multi={column.multi}
              inputGroupEmbedded
              disabled={disabled}
              value={cell}
              fieldId={column._id}
              title={column.title}
              enableSystemFilePicker={enableSystemFilePicker}
              primaryMaxWidth={column.maxImageWidth}
              primaryMaxHeight={column.maxImageHeight}
              onFileUpload={(f, i, p, value, loadingCallback, errorCallback) => {
                this.handleCellUpload(rowId, column._id, value, loadingCallback, errorCallback);
              }}
              onFileClick={(f, i, p, urlObject, urlIndex) => {
                if (onCellFileClick) onCellFileClick(rowId, column._id, urlObject, urlIndex);
              }}
              onFileDelete={(f, i, p, urlObject, urlIndex) => {
                if (onCellFileDelete) onCellFileDelete(rowId, column._id, urlObject, urlIndex);
              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
              onFocus={() => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, {});
              }}
              onBlur={(e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              {...styles}
              padding={2}
              measure={!!onLoadCompleted}
              onMeasureCompleted={() => this.handleLoadCompleted(column._id)}
            />

          </StyledFieldWrapper>
        );
      } else if (column.type === FieldTableColumnTypes.multiSelect) {
        const isMultiselectEmpty = !cell || (Array.isArray(cell) && cell.length === 0);
        const multiselectValueCount = Array.isArray(cell) ? cell.length : 0;

        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="multiselect"
            data-text-overflow={textOverflow || ''}
            data-empty={isMultiselectEmpty ? 'true' : undefined}
            data-readonly={disabled ? 'true' : undefined}
            data-value-count={multiselectValueCount}
            {...fieldWrapperStyles}
          >
            <MultiselectGenericField
              id={testId}
              fieldId={column._id}
              type="text"
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={cell || ''}
              options={column.options}
              compactMode
              inputGroupWrapMode={inputGroupWrapMode}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {

                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);

              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      } else if (column.type === FieldTableColumnTypes.barcode) {

        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="barcode"
            data-text-overflow={textOverflow || ''}
            {...fieldWrapperStyles}
          >
            <BarcodeGenericField
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={isFocused ? cell : calculatedCell}
              wrapperStyles={{ height: 'auto' }}
              inputGroupWrapMode={inputGroupWrapMode}
              {...styles}
              onCapture={onCapture}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, column.type, e);
              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      }
      else if (column.type === FieldTableColumnTypes.block) {

        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="block"
            data-text-overflow={textOverflow || ''}
            {...fieldWrapperStyles}
          >
            <BlockGenericField
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              fieldMode={fieldMode}
              value={isFocused ? cell : calculatedCell}
              inputGroupWrapMode={inputGroupWrapMode}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, column.type, e);
              }}
              onChange={(f, i, p, value) => { if (onCellChange) onCellChange(rowId, column._id, value); }}
            />
          </StyledFieldWrapper>
        );
      }
      else if (column.type === FieldTableColumnTypes.signature) {

        /**
         * If the column width is less than maxImageWidth then the column width
         * will be applied as the maxImageWidth so that images would be placed within
         * cell boundary
        */
        const validMaxImageWidth = columnWidth && columnWidth < column.maxImageWidth ? columnWidth : column.maxImageWidth;
        const isSignatureEmpty = !cell && !calculatedCell;

        return (

          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="signature"
            data-text-overflow={textOverflow || ''}
            data-empty={isSignatureEmpty ? 'true' : undefined}
            {...fieldWrapperStyles}
          >
            <SignatureField
              hideButton
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled || fieldMode === FieldModes.edit}
              focused={isFocused}
              value={isFocused ? cell : calculatedCell}
              wrapperStyles={{ height: 'auto' }}
              {...styles}
              onCapture={onCapture}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              onChange={(f, i, p, value, signer) => {
                if (onCellChange) onCellChange(rowId, column._id, value, undefined, signer);
              }}
              maxImageWidth={validMaxImageWidth}
              maxImageHeight={column.maxImageHeight}
              onLoadCompleted={() => this.handleLoadCompleted(column._id)}
            />
          </StyledFieldWrapper>
        );
      }
      else if (column.type === FieldTableColumnTypes.date) {

        let nextValue = isFocused ? cell : calculatedCell;

        if (column.type === FieldTableColumnTypes.date && nextValue) {

          /**
           * Moment timestamp appears as a string in the calculatedCellValue
           * which is used to display the date when cell is not focused. We convert
           * it to number to ensure that the date is displayed in input group
           * dateCell
           */

          nextValue = Number(nextValue);

        }

        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="date"
            data-text-overflow={textOverflow || ''}
            {...fieldWrapperStyles}
          >
            <DateField
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={nextValue}
              wrapperStyles={{ height: 'auto' }}
              tz={row.tz}
              format={fieldType === FieldTypes.collection ? column.format : fieldPositionColumn.format}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              onChange={(f, i, p, value, signer, collectionFieldParams, eventType, timezone) => {
                if (onCellChange) onCellChange(rowId, column._id, value, timezone);
              }}
            />
          </StyledFieldWrapper>
        );
      }
      else {

        let nextValue = isFocused ? cell : calculatedCell;

        if (column.type === FieldTableColumnTypes.date && nextValue) {

          /**
           * Moment timestamp appears as a string in the calculatedCellValue
           * which is used to display the date when cell is not focused. We convert
           * it to number to ensure that the date is displayed in input group
           * dateCell
           */

          nextValue = Number(nextValue);

        }

        /**
         * <------------------------NOTICE---------------->
         * We are passing type to ensure an empty string is set as value inside text field
         */
        return (
          <StyledFieldWrapper
            key={cellKey}
            data-testid={`${testId}-input-group-field-field-wrapper`}
            data-field-type="text"
            data-text-overflow={textOverflow || ''}
            {...fieldWrapperStyles}
          >
            <TextField
              id={testId}
              type={column.type}
              fieldId={column._id}
              title={column.title}
              disabled={disabled}
              focused={isFocused}
              value={nextValue}
              wrapperStyles={{ height: 'auto' }}
              tz={row.tz}
              format={fieldType === FieldTypes.collection ? column.format : fieldPositionColumn.format}
              inputGroupWrapMode={inputGroupWrapMode}
              {...styles}
              onFocus={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellFocus) onCellFocus(rowId, column._id, column.identifier, column.type, e);
              }}
              onBlur={(fieldId, fieldIdentifier, fieldPositionId, e) => {
                if (onCellBlur) onCellBlur(rowId, column._id, column.identifier, e);
              }}
              onChange={(f, i, p, value, signer, collectionFieldParams, eventType, timezone) => {
                if (onCellChange) onCellChange(rowId, column._id, value, timezone);
              }}
            />
          </StyledFieldWrapper>
        );
      }

    });

    return (
      <StyledWrapper
        ref={(e) => {
          if (e && !rowRef) this.setState({ rowRef: e });
        }}
        key={row._id}
      >
        {!enableTitle ? null : (
          <StyledTitleWrapper>
            <StyledTitle>
              <StyledFieldHeader>
                <FieldTitle
                  {...{
                    title,
                    titleDisplay,
                    titleFontSize,
                    titleFontColor,
                    titleTextAlign,
                    titleFontStyle,
                    titleFontWeight,
                    titleTextTransform,
                    titleTextDecoration,
                  }}
                />
                <FieldDecorators
                  decorators={decorators}
                  fieldMode={fieldMode}
                  fieldId={fieldId}
                  fieldIdentifier={fieldIdentifier}
                  fieldPositionId={fieldPositionId}
                  onDecoratorClick={onDecoratorClick}
                />
              </StyledFieldHeader>
            </StyledTitle>
          </StyledTitleWrapper>
        )}
        <StyledRow
          key={row._id}
          data-testid={`${id}-${row._id}-${rowIndex}-input-group-field-row`}
          {...{
            backgroundColor: columnTitleBackgroundColor,
            padding: columnTitlePadding,
            border,
            borderRadius,
            textOverflow,
          }}
        >
          {(enableRowDecorators || (!disabled && enableMenu)) ? (
            <StyledRowActions>
              {enableRowDecorators ? (
                <InputGroupRowDecorators
                  decorators={rowDecorators}
                  fieldMode={fieldMode}
                  rowId={row._id}
                  onDecoratorClick={onRowDecoratorClick}
                />
              ) : null}
              {disabled || !enableMenu ? null : (
                <StyledSelect
                  {...{
                    fontColor,
                    borderColor,
                    borderRadius,
                    primaryColor
                  }}
                  value="cog"
                  disabled={disabled}
                  onChange={this.handleMenuSelection}
                  data-testid={`${id}-${row._id}-input-group-field-row-menu`}
                >
                  <option value="cog" />
                  {rowActions?.map((option) => (
                    <option key={option._id} value={option._id}>{option.value}</option>
                  ))}
                </StyledSelect>
              )}
            </StyledRowActions>
          ) : null}
          {displayRequiredIndicators ? (<Icon icon={ThemeIcons.settings.required} color={dangerColor} />) : null}
          {cells}
        </StyledRow>
      </StyledWrapper>
    );

  }

  handleLoadCompleted = (columnId) => {

    const { imageColumnsLoaded } = this.state;
    if (!imageColumnsLoaded[columnId]) this.setState((prevState) => ({ imageColumnsLoaded: { ...prevState.imageColumnsLoaded, [columnId]: true } }));

  }

  handleCellUpload = (rowId, columnId, value, loadingCallback, errorCallback) => {

    const { onCellFileUpload } = this.props;
    const { imageColumnsLoaded } = this.state;

    const nextImageColumnsLoaded = { ...imageColumnsLoaded };
    delete nextImageColumnsLoaded[columnId];

    this.setState({ imageColumnsLoaded: nextImageColumnsLoaded });

    if (onCellFileUpload) onCellFileUpload(rowId, columnId, value, loadingCallback, errorCallback);

  }

  handleMenuSelection = (e) => {

    const { onRowAction, row, rowIndex, rowPath } = this.props;
    const option = e.target.value;

    onRowAction(option, row._id, rowIndex, rowPath);

  }

}

export const getTableImageColumns = createSelector(
  (state) => state.columns,
  (columns) => {

    /**
     * Return all image columns that exist inside the tableColumnOrder
     */
    return columns.filter((column) => column.type === FieldTableColumnTypes.image || column.type === FieldTableColumnTypes.signature);

  }
);

const shouldComponentUpdate = (nextProps, props, nextState, state) => {

  const { rowActions, focused, columns, row, disabled, title, rowDecorators } = props;
  const { imageColumnsLoaded, rowRef } = state;

  /**
   * Row is impacted if either the previously editing cell belonged to this
   * row or the next editing cell belongs to this row.
   */
  const editingCellImpactsRow = (focused && focused.indexOf(row._id) !== -1)
    || (nextProps.focused && nextProps.focused.indexOf(row._id) !== -1);

  const stylesChanged = computeStyleChanges(nextProps, props);

  /**
   * Only pass table lookup for each row.
   */
  if (
    (focused !== nextProps.focused && editingCellImpactsRow)
    || disabled !== nextProps.disabled
    || !isEqual(columns, nextProps.columns)
    || !isEqual(row, nextProps.row)
    || stylesChanged
    || title !== nextProps.title
    || rowActions !== nextProps.rowActions
    || rowDecorators !== nextProps.rowDecorators
    || rowRef !== nextState.rowRef
    || imageColumnsLoaded !== nextState.imageColumnsLoaded
  ) {
    return true;
  } else {
    return false;
  }

};

export { shouldComponentUpdate };

export default InputGroupFieldRow;
